import { toaster } from '@/shared/ui/toaster.component';
import {
  Avatar,
  Button,
  DataTable,
  DataTableColumn,
  Menu,
  Pagination,
  Tag,
} from '@noorden-insurtech/alba-design-system';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Ban, Edit, KeyRound, Mail, Plus, Send, UserCheck } from 'lucide-react';
import { parseAsBoolean, useQueryState } from 'nuqs';
import { useState } from 'react';
import { CreateUserModal } from '../../../features/users/components/create-user.modal';
import { EditUserModal } from '../../../features/users/components/edit-user.modal';
import {
  useActivateUser,
  useAdminResetPassword,
  getResendFirstAccessErrorToast,
  useDeactivateUser,
  useResendFirstAccess,
  useUsers,
} from '../../../features/users/hooks';
import type { User } from '../../../services';
import { ROLE_COLOR_SCHEME_MAPPING } from '../../../shared/constants/roles.constants';
import { useRequirePermissions } from '../../../shared/hooks';
import { SearchFilterField, SelectFilterField } from '../../../shared/ui';
import { f, requirePermissions, useFilters } from '../../../shared/utils';
import { formatRegisterNumber } from '../../../shared/utils/format-register-number';
import {
  UserGroup,
  getScopeFromGroupName,
} from '../../../shared/utils/get-group';
import { getRoleLabel } from '../../../shared/utils/get-role-label';
import { getUserStatus } from '../../../shared/utils/get-user-status';

export const Route = createFileRoute('/_app/settings/users')({
  component: SettingsUsersRoute,
  beforeLoad: async () => {
    const hasPermission = await requirePermissions(['identity.users.read']);
    if (!hasPermission) {
      throw redirect({ to: '/dashboard' });
    }
  },
});

function SettingsUsersRoute() {
  const { hasPermission: canCreateUser } = useRequirePermissions({
    permissions: ['identity.users.write'],
  });
  const { hasPermission: canEditUser } = useRequirePermissions({
    permissions: ['identity.users.write'],
  });
  const { hasPermission: canManageStatus } = useRequirePermissions({
    permissions: ['identity.users.write'],
  });
  const { hasPermission: canResendFirstAccess } = useRequirePermissions({
    permissions: [
      'identity.users.write',
      'parties.brokers.users.write',
      'parties.advisories.users.write',
    ],
  });
  const { hasPermission: canResetPassword } = useRequirePermissions({
    permissions: [
      'identity.users.write',
      'parties.brokers.users.write',
      'parties.advisories.users.write',
    ],
  });

  const [newUser, setNewUser] = useQueryState(
    'newUser',
    parseAsBoolean.withDefault(false)
  );
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const { filters, debouncedFilters, setFilters, registerFilter } = useFilters({
    search: f.string().withDefault('').withDebounce(500),
    role: f.string().withDefault(''),
    page: f.integer().withDefault(1),
    pageSize: f.integer().withDefault(5),
  });

  const { data, isLoading, error, refetch } = useUsers({
    limit: debouncedFilters.pageSize,
    ...debouncedFilters,
  });

  const { mutateAsync: deactivateUser, isPending: isDeactivating } =
    useDeactivateUser();
  const { mutateAsync: activateUser, isPending: isActivating } =
    useActivateUser();
  const { mutateAsync: resendFirstAccess, isPending: isResendingFirstAccess } =
    useResendFirstAccess();
  const { mutateAsync: adminResetPassword, isPending: isResettingPassword } =
    useAdminResetPassword();

  // Column definitions for DataTable
  const columns: DataTableColumn<User>[] = [
    {
      accessorKey: 'name',
      header: 'Nome',
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className='flex items-center gap-3'>
            <Avatar.Root size='md' shape='circle'>
              <Avatar.Fallback />
            </Avatar.Root>
            <div className='flex flex-col gap-1'>
              <span className='font-gotham font-bold text-alba-black-500'>
                {user.name}
              </span>
              <div className='flex items-center gap-1'>
                <Mail className='w-4 h-4 text-alba-gray-600' />
                <span className='text-alba-gray-600 text-xs'>{user.email}</span>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'registerNumber',
      header: 'CPF',
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className='flex items-center gap-2'>
            <span className='text-alba-gray-700 text-sm'>
              {formatRegisterNumber(user.registerNumber)}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const user = row.original;
        const statusText = getUserStatus(user.status, user.deactivated_at);
        const isActive =
          statusText === 'Ativo' || statusText === 'Primeiro acesso';

        return (
          <div className='flex items-center gap-2'>
            <span
              className={`w-2.5 h-2.5 rounded-full ${
                isActive ? 'bg-alba-success-green-500' : 'bg-alba-gray-400'
              }`}
            />
            <span className='text-alba-gray-700 text-sm'>{statusText}</span>
          </div>
        );
      },
    },
    {
      id: 'scope',
      accessorKey: 'groupName',
      header: 'Escopo',
      cell: ({ row }) => {
        const user = row.original;
        return (
          <span className='text-alba-gray-700 text-sm'>
            {getScopeFromGroupName(user.groupName)}
          </span>
        );
      },
    },
    {
      id: 'profile',
      accessorKey: 'groupName',
      header: 'Perfil',
      sortable: true,
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className=''>
            <Tag.Root
              variant='outline'
              colorScheme={
                ROLE_COLOR_SCHEME_MAPPING[user.profile || 'Administrador']
              }
              size='lg'
            >
              <Tag.Label>{getRoleLabel(user.groupName)}</Tag.Label>
            </Tag.Root>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      size: 50,
      sortable: false,
      cell: ({ row }) => {
        const user = row.original;
        const hasAnyAction =
          canEditUser || canManageStatus || canResendFirstAccess || canResetPassword;
        if (!hasAnyAction) return null;
        const statusText = getUserStatus(user.status, user.deactivated_at);
        const isActive =
          statusText === 'Ativo' || statusText === 'Primeiro acesso';
        return (
          <div className='flex justify-end'>
            <Menu.Root
              key={`${user.email}-${user.status}-${user.deactivated_at ?? ''}`}
            >
              <Menu.Trigger />
              <Menu.Content>
                {canEditUser && (
                  <Menu.Item
                    value='edit'
                    icon={<Edit size={16} />}
                    onSelect={() => setEditingUser(user)}
                  >
                    Editar
                  </Menu.Item>
                )}
                {canResendFirstAccess &&
                  user.status === 'FORCE_CHANGE_PASSWORD' && (
                    <Menu.Item
                      value='resend'
                      icon={<Send size={16} />}
                      disabled={isResendingFirstAccess}
                      onSelect={async () => {
                        try {
                          await resendFirstAccess(user.email);
                          await refetch();
                          toaster.create({
                            type: 'success',
                            title: 'Convite reenviado',
                            description:
                              'O link do primeiro acesso foi reenviado por email.',
                          });
                        } catch (error) {
                          toaster.create(getResendFirstAccessErrorToast(error));
                        }
                      }}
                    >
                      Reenviar convite
                    </Menu.Item>
                  )}
                {canResetPassword &&
                  isActive &&
                  user.status !== 'FORCE_CHANGE_PASSWORD' && (
                  <Menu.Item
                    value='reset-password'
                    icon={<KeyRound size={16} />}
                    disabled={isResettingPassword}
                    onSelect={async () => {
                      try {
                        await adminResetPassword(user.email);
                        await refetch();
                        toaster.create({
                          type: 'success',
                          title: 'Link enviado',
                          description:
                            'O usuário receberá um email com link para redefinir a senha.',
                        });
                      } catch (error) {
                        const msg = (
                          error instanceof Error
                            ? error.message
                            : 'Falha ao enviar link de redefinição'
                        ).trim();
                        toaster.create({
                          type: 'error',
                          title: 'Erro ao enviar link',
                          description: msg,
                        });
                      }
                    }}
                  >
                    Resetar senha
                  </Menu.Item>
                )}
                {canManageStatus && isActive && (
                  <Menu.Item
                    value='disable'
                    icon={<Ban size={16} />}
                    disabled={isDeactivating}
                    onSelect={async () => {
                      try {
                        await deactivateUser(user.email);
                        await refetch();
                        toaster.create({
                          type: 'success',
                          title: 'Usuário desativado',
                          description: 'O usuário foi desativado com sucesso.',
                        });
                      } catch (error) {
                        const msg = (
                          error instanceof Error
                            ? error.message
                            : 'Falha ao desativar usuário'
                        ).trim();
                        toaster.create({
                          type: 'error',
                          title: 'Erro ao desativar usuário',
                          description: msg,
                        });
                      }
                    }}
                  >
                    Desativar
                  </Menu.Item>
                )}
                {canManageStatus && !isActive && (
                  <Menu.Item
                    value='activate'
                    icon={<UserCheck size={16} />}
                    disabled={isActivating}
                    onSelect={async () => {
                      try {
                        await activateUser(user.email);
                        await refetch();
                        toaster.create({
                          type: 'success',
                          title: 'Usuário ativado',
                          description: 'O usuário foi ativado com sucesso.',
                        });
                      } catch (error) {
                        const msg = (
                          error instanceof Error
                            ? error.message
                            : 'Falha ao ativar usuário'
                        ).trim();
                        toaster.create({
                          type: 'error',
                          title: 'Erro ao ativar usuário',
                          description: msg,
                        });
                      }
                    }}
                  >
                    Ativar
                  </Menu.Item>
                )}
              </Menu.Content>
            </Menu.Root>
          </div>
        );
      },
    },
  ];

  const roleOptions = [
    { label: 'Todos', value: '' },
    ...Object.values(UserGroup)
      .map(groupName => ({
        label: `${getScopeFromGroupName(groupName)} - ${getRoleLabel(groupName)}`,
        value: groupName,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
  ];

  return (
    <div className='h-full overflow-auto'>
      <div className='flex items-center justify-between gap-4'></div>

      <div className='flex flex-col gap-3 mb-6 pr-1 md:flex-row md:items-center md:justify-between md:gap-4 lg:gap-x-16'>
        <div className='flex flex-col flex-1 gap-3 md:flex-row md:gap-4'>
          <SearchFilterField
            {...registerFilter('search')}
            placeholder='Buscar usuários...'
          />
          <SelectFilterField
            {...registerFilter('role')}
            items={roleOptions}
            placeholder='Perfil'
            onValueChange={value =>
              setFilters({ ...filters, role: value, page: 1 })
            }
            className='w-full md:min-w-[240px] md:w-auto md:shrink-0'
          />
        </div>
        {canCreateUser && (
          <Button
            variant='primary'
            size='md'
            onClick={() => setNewUser(true)}
            className='w-full md:w-auto'
          >
            <Plus className='h-5 w-5' strokeWidth={2} />
            Adicionar usuário
          </Button>
        )}
      </div>

      <div className='flex-1 min-h-0 flex flex-col'>
        <div className='flex-1 min-h-0'>
          {error ? (
            <div className='py-8 text-center text-alba-error-red-500'>
              Erro ao carregar usuários. Tente novamente.
            </div>
          ) : (
            <>
              <div className='space-y-3 md:hidden'>
                {(data?.data || []).map(user => {
                  const statusText = getUserStatus(
                    user.status,
                    user.deactivated_at
                  );
                  const isActive =
                    statusText === 'Ativo' || statusText === 'Primeiro acesso';
                  const hasAnyAction =
                    canEditUser ||
                    canManageStatus ||
                    canResendFirstAccess ||
                    canResetPassword;

                  return (
                    <article
                      key={user.email}
                      className='rounded-2xl bg-white p-4 shadow-[0px_2px_10px_0px_rgba(15,22,30,0.06)]'
                    >
                      <div className='flex items-start justify-between gap-3'>
                        <div className='flex min-w-0 items-center gap-3'>
                          <Avatar.Root size='md' shape='circle'>
                            <Avatar.Fallback />
                          </Avatar.Root>
                          <div className='min-w-0'>
                            <p className='truncate font-gotham font-bold text-alba-black-500'>
                              {user.name}
                            </p>
                            <div className='mt-1 flex items-center gap-1'>
                              <Mail className='h-4 w-4 text-alba-gray-600' />
                              <span className='truncate text-xs text-alba-gray-600'>
                                {user.email}
                              </span>
                            </div>
                          </div>
                        </div>
                        {hasAnyAction && (
                          <Menu.Root
                            key={`${user.email}-${user.status}-${user.deactivated_at ?? ''}`}
                          >
                            <Menu.Trigger />
                            <Menu.Content>
                              {canEditUser && (
                                <Menu.Item
                                  value='edit'
                                  icon={<Edit size={16} />}
                                  onSelect={() => setEditingUser(user)}
                                >
                                  Editar
                                </Menu.Item>
                              )}
                              {canResendFirstAccess &&
                                user.status === 'FORCE_CHANGE_PASSWORD' && (
                                  <Menu.Item
                                    value='resend'
                                    icon={<Send size={16} />}
                                    disabled={isResendingFirstAccess}
                                    onSelect={async () => {
                                      try {
                                        await resendFirstAccess(user.email);
                                        await refetch();
                                        toaster.create({
                                          type: 'success',
                                          title: 'Convite reenviado',
                                          description:
                                            'O link do primeiro acesso foi reenviado por email.',
                                        });
                                      } catch (error) {
                                        toaster.create(
                                          getResendFirstAccessErrorToast(error)
                                        );
                                      }
                                    }}
                                  >
                                    Reenviar convite
                                  </Menu.Item>
                                )}
                              {canResetPassword &&
                                isActive &&
                                user.status !== 'FORCE_CHANGE_PASSWORD' && (
                                  <Menu.Item
                                    value='reset-password'
                                    icon={<KeyRound size={16} />}
                                    disabled={isResettingPassword}
                                    onSelect={async () => {
                                      try {
                                        await adminResetPassword(user.email);
                                        await refetch();
                                        toaster.create({
                                          type: 'success',
                                          title: 'Link enviado',
                                          description:
                                            'O usuário receberá um email com link para redefinir a senha.',
                                        });
                                      } catch (error) {
                                        const msg = (
                                          error instanceof Error
                                            ? error.message
                                            : 'Falha ao enviar link de redefinição'
                                        ).trim();
                                        toaster.create({
                                          type: 'error',
                                          title: 'Erro ao enviar link',
                                          description: msg,
                                        });
                                      }
                                    }}
                                  >
                                    Resetar senha
                                  </Menu.Item>
                                )}
                              {canManageStatus && isActive && (
                                <Menu.Item
                                  value='disable'
                                  icon={<Ban size={16} />}
                                  disabled={isDeactivating}
                                  onSelect={async () => {
                                    try {
                                      await deactivateUser(user.email);
                                      await refetch();
                                      toaster.create({
                                        type: 'success',
                                        title: 'Usuário desativado',
                                        description:
                                          'O usuário foi desativado com sucesso.',
                                      });
                                    } catch (error) {
                                      const msg = (
                                        error instanceof Error
                                          ? error.message
                                          : 'Falha ao desativar usuário'
                                      ).trim();
                                      toaster.create({
                                        type: 'error',
                                        title: 'Erro ao desativar usuário',
                                        description: msg,
                                      });
                                    }
                                  }}
                                >
                                  Desativar
                                </Menu.Item>
                              )}
                              {canManageStatus && !isActive && (
                                <Menu.Item
                                  value='activate'
                                  icon={<UserCheck size={16} />}
                                  disabled={isActivating}
                                  onSelect={async () => {
                                    try {
                                      await activateUser(user.email);
                                      await refetch();
                                      toaster.create({
                                        type: 'success',
                                        title: 'Usuário ativado',
                                        description:
                                          'O usuário foi ativado com sucesso.',
                                      });
                                    } catch (error) {
                                      const msg = (
                                        error instanceof Error
                                          ? error.message
                                          : 'Falha ao ativar usuário'
                                      ).trim();
                                      toaster.create({
                                        type: 'error',
                                        title: 'Erro ao ativar usuário',
                                        description: msg,
                                      });
                                    }
                                  }}
                                >
                                  Ativar
                                </Menu.Item>
                              )}
                            </Menu.Content>
                          </Menu.Root>
                        )}
                      </div>

                      <div className='mt-3 h-px w-full bg-alba-gray-200' />

                      <div className='mt-3 flex flex-col gap-2 text-sm'>
                        <div className='flex items-center justify-between gap-2'>
                          <span className='text-alba-gray-600'>CPF</span>
                          <span className='text-alba-gray-700'>
                            {formatRegisterNumber(user.registerNumber)}
                          </span>
                        </div>
                        <div className='flex items-center justify-between gap-2'>
                          <span className='text-alba-gray-600'>Escopo</span>
                          <span className='text-alba-gray-700'>
                            {getScopeFromGroupName(user.groupName)}
                          </span>
                        </div>
                        <div className='flex items-center justify-between gap-2'>
                          <span className='text-alba-gray-600'>Perfil</span>
                          <Tag.Root
                            variant='outline'
                            colorScheme={
                              ROLE_COLOR_SCHEME_MAPPING[
                                user.profile || 'Administrador'
                              ]
                            }
                            size='sm'
                          >
                            <Tag.Label>{getRoleLabel(user.groupName)}</Tag.Label>
                          </Tag.Root>
                        </div>
                        <div className='flex items-center justify-between gap-2'>
                          <span className='text-alba-gray-600'>Status</span>
                          <div className='flex items-center gap-2'>
                            <span
                              className={`h-2.5 w-2.5 rounded-full ${
                                isActive
                                  ? 'bg-alba-success-green-500'
                                  : 'bg-alba-gray-400'
                              }`}
                            />
                            <span className='text-alba-gray-700'>
                              {statusText}
                            </span>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}

                {!isLoading && (data?.data || []).length === 0 && (
                  <div className='rounded-2xl bg-white py-8 text-center text-alba-gray-600 shadow-[0px_2px_10px_0px_rgba(15,22,30,0.06)]'>
                    Nenhum resultado encontrado.
                  </div>
                )}

                {(data?.meta.totalPages ?? 0) > 0 && (
                  <div className='origin-center scale-90'>
                    <Pagination
                      className='justify-center gap-1 [&>div]:gap-1'
                      count={(data?.meta.totalPages ?? 0) * filters.pageSize}
                      page={filters.page}
                      pageSize={filters.pageSize}
                      totalItems={data?.meta.total ?? 0}
                      pageSizeOptions={[5, 10, 20, 30, 40, 50]}
                      onPageChange={p =>
                        setFilters({
                          ...filters,
                          page: p.page,
                          pageSize: p.pageSize,
                        })
                      }
                      onPageSizeSelect={pageSize =>
                        setFilters({ ...filters, page: 1, pageSize })
                      }
                    />
                  </div>
                )}
              </div>

              <div className='hidden md:block'>
                <DataTable
                  loading={isLoading}
                  columns={columns}
                  pageSizeOptions={[5, 10, 20, 30, 40, 50]}
                  totalPages={data?.meta.totalPages || 0}
                  data={data?.data || []}
                  pageSize={filters.pageSize}
                  totalItems={data?.meta.total || 0}
                  onPageChange={(page, pageSize) =>
                    setFilters({ ...filters, page, pageSize })
                  }
                />
              </div>
            </>
          )}
        </div>
      </div>

      <CreateUserModal
        onSuccess={() => {
          setNewUser(false);
          setFilters({ ...filters, page: 1 });
        }}
        isOpen={newUser}
        onClose={() => setNewUser(false)}
      />

      <EditUserModal
        userId={null}
        initialUser={editingUser}
        isOpen={Boolean(editingUser)}
        onClose={() => setEditingUser(null)}
        onSuccess={() => {
          setEditingUser(null);
          refetch();
        }}
      />
    </div>
  );
}
